import { api } from './index';

export type TypeformAdminSubmissionStatus = 'active' | 'archived';

export interface TypeformAdminSubmission {
  id: string;
  formType: string;
  typeformFormId: string;
  respondentEmail?: string | null;
  respondentFirstName?: string | null;
  respondentLastName?: string | null;
  respondentCompany?: string | null;
  status: TypeformAdminSubmissionStatus;
  submittedAt: string;
}

export interface TypeformAdminSubmissionDetail extends TypeformAdminSubmission {
  answers: unknown[];
  calculated?: Record<string, unknown> | null;
  createdAt: string;
  definition?: Record<string, unknown> | null;
  hidden?: Record<string, unknown> | null;
  linkedPersonId?: string | null;
  pulseReport?: {
    id: string;
    status: 'active' | 'hidden';
  } | null;
  typeformEventId: string;
  typeformResponseToken: string;
  updatedAt: string;
  variables?: Array<Record<string, unknown>> | Record<string, unknown> | null;
}

export interface TypeformAdminSubmissionsResponse {
  submissions: TypeformAdminSubmission[];
  total: number;
  page: number;
  per: number;
}

export interface GetTypeformAdminSubmissionsParams {
  page?: number;
  per?: number;
  q?: string;
  formType?: string;
  status?: TypeformAdminSubmissionStatus;
  company?: string;
}

export interface TypeformAdminFormSummary {
  typeformFormId: string;
  formType: string;
  formTitle?: string | null;
  pulseReportable: boolean;
  submissionCount: number;
  hasMappings: boolean;
  unmappedCount: number;
  latestSubmissionAt?: string | null;
}

export interface TypeformAdminFormsResponse {
  forms: TypeformAdminFormSummary[];
}

export interface TypeformAdminAvailableField {
  id?: string | null;
  ref?: string | null;
  title?: string | null;
  type?: string | null;
}

export interface TypeformAdminFieldMapping {
  id?: string;
  fieldPurpose: 'email' | 'first_name' | 'last_name' | 'company';
  typeformFieldRef: string;
  typeformFieldId?: string | null;
  fieldTitle?: string | null;
}

export interface TypeformAdminFormMappingsResponse {
  typeformFormId: string;
  formType?: string | null;
  mappings: TypeformAdminFieldMapping[];
  availableFields: TypeformAdminAvailableField[];
}

export interface TypeformAdminFormFieldGroup {
  id?: string;
  typeformFieldRef: string;
  fieldGroup: 'identity' | 'onboarding' | 'pulse' | 'demographic' | 'manager';
  canonicalRef?: string | null;
  fieldTitle?: string | null;
}

export interface TypeformAdminFormGroupsResponse {
  typeformFormId: string;
  formType?: string | null;
  groups: TypeformAdminFormFieldGroup[];
  availableFields: TypeformAdminAvailableField[];
  canonicalRefs: string[];
  validGroups: TypeformAdminFormFieldGroup['fieldGroup'][];
}

export interface TypeformAdminImportResult {
  imported: number;
  skipped: number;
  failed: number;
  backfilled?: number | null;
}

export interface TypeformAdminImportAllResult {
  forms: number;
  formErrors: number;
  perForm: Record<string, TypeformAdminImportResult | { error: string }>;
  total: {
    imported: number;
    skipped: number;
    failed: number;
    backfilled: number;
  };
}

export interface TypeformAdminBackfillResult {
  total: number;
  updated: number;
  failed: number;
}

export interface TypeformAdminReclassifyResult {
  reclassified: number;
  backfilled: number;
  formType: string;
}

export interface TypeformAdminCatalogSyncResult {
  formsSeen: number;
  created: number;
  updated: number;
  archived: number;
}

export interface TypeformAdminDefinitionSyncResult {
  typeformFormId: string;
  formType: string;
  formTitle?: string | null;
  lastDefinitionSyncedAt?: string | null;
}

export interface TypeformAdminCatalogUpdateResult {
  form: TypeformAdminFormSummary;
}

export const typeformAdminApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTypeformAdminSubmissions: builder.query<
      TypeformAdminSubmissionsResponse,
      GetTypeformAdminSubmissionsParams | void
    >({
      providesTags: (result) => [
        'TypeformAdminSubmission',
        ...(result?.submissions.map(({ id }) => ({
          type: 'TypeformAdminSubmission' as const,
          id,
        })) ?? []),
      ],
      query: (params = {}) => ({
        method: 'GET',
        params,
        url: '/admin/onboarding/submissions',
      }),
    }),
    getTypeformAdminSubmission: builder.query<
      TypeformAdminSubmissionDetail,
      string
    >({
      providesTags: (_result, _error, id) => [
        { type: 'TypeformAdminSubmission' as const, id },
      ],
      query: (id) => ({
        method: 'GET',
        url: `/admin/onboarding/submissions/${id}`,
      }),
      transformResponse: (response: {
        submission: TypeformAdminSubmissionDetail;
      }) => response.submission,
    }),
    linkTypeformAdminSubmission: builder.mutation<
      TypeformAdminSubmissionDetail,
      { id: string; personId: string }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TypeformAdminSubmission' as const, id },
        'TypeformAdminSubmission',
      ],
      query: ({ id, personId }) => ({
        method: 'PATCH',
        data: { personId },
        url: `/admin/onboarding/submissions/${id}/link`,
      }),
      transformResponse: (response: {
        submission: TypeformAdminSubmissionDetail;
      }) => response.submission,
    }),
    unlinkTypeformAdminSubmission: builder.mutation<
      TypeformAdminSubmissionDetail,
      { id: string }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TypeformAdminSubmission' as const, id },
        'TypeformAdminSubmission',
      ],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/admin/onboarding/submissions/${id}/link`,
      }),
      transformResponse: (response: {
        submission: TypeformAdminSubmissionDetail;
      }) => response.submission,
    }),
    updateTypeformAdminSubmissionStatus: builder.mutation<
      TypeformAdminSubmissionDetail,
      { id: string; status: TypeformAdminSubmissionStatus }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TypeformAdminSubmission' as const, id },
        'TypeformAdminSubmission',
      ],
      query: ({ id, status }) => ({
        method: 'PATCH',
        data: { status },
        url: `/admin/onboarding/submissions/${id}/status`,
      }),
      transformResponse: (response: {
        submission: TypeformAdminSubmissionDetail;
      }) => response.submission,
    }),
    exportTypeformAdminSubmissions: builder.mutation<
      Blob,
      GetTypeformAdminSubmissionsParams | void
    >({
      query: (params = {}) => ({
        method: 'GET',
        params,
        responseType: 'blob',
        url: '/admin/onboarding/submissions/export',
      }),
    }),
    getTypeformAdminForms: builder.query<TypeformAdminFormsResponse, void>({
      providesTags: (result) => [
        'TypeformAdminForm',
        ...(result?.forms.map(({ typeformFormId }) => ({
          type: 'TypeformAdminForm' as const,
          id: typeformFormId,
        })) ?? []),
      ],
      query: () => ({
        method: 'GET',
        url: '/admin/onboarding/forms',
      }),
    }),
    syncTypeformAdminCatalog: builder.mutation<
      TypeformAdminCatalogSyncResult,
      void
    >({
      invalidatesTags: ['TypeformAdminForm'],
      query: () => ({
        method: 'POST',
        url: '/admin/onboarding/forms/sync_catalog',
      }),
    }),
    updateTypeformAdminFormCatalog: builder.mutation<
      TypeformAdminCatalogUpdateResult,
      {
        formId: string;
        form: {
          formType?: string;
          pulseReportable?: boolean;
          visible?: boolean;
        };
      }
    >({
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
      ],
      query: ({ formId, form }) => ({
        method: 'PATCH',
        data: { form },
        url: `/admin/onboarding/forms/${formId}/catalog`,
      }),
    }),
    importAllTypeformAdminForms: builder.mutation<
      TypeformAdminImportAllResult,
      void
    >({
      invalidatesTags: ['TypeformAdminForm', 'TypeformAdminSubmission'],
      query: () => ({
        method: 'POST',
        url: '/admin/onboarding/forms/import_all',
      }),
    }),
    getTypeformAdminFormMappings: builder.query<
      TypeformAdminFormMappingsResponse,
      string
    >({
      providesTags: (_result, _error, formId) => [
        { type: 'TypeformAdminForm' as const, id: formId },
      ],
      query: (formId) => ({
        method: 'GET',
        url: `/admin/onboarding/forms/${formId}/mappings`,
      }),
    }),
    setTypeformAdminFormMappings: builder.mutation<
      { mappings: TypeformAdminFieldMapping[] },
      { formId: string; mappings: TypeformAdminFieldMapping[] }
    >({
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
      ],
      query: ({ formId, mappings }) => ({
        method: 'PUT',
        data: { mappings },
        url: `/admin/onboarding/forms/${formId}/mappings`,
      }),
    }),
    importTypeformAdminForm: builder.mutation<
      TypeformAdminImportResult,
      { formId: string; formType: string }
    >({
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
        'TypeformAdminSubmission',
      ],
      query: ({ formId, formType }) => ({
        method: 'POST',
        data: { formType },
        url: `/admin/onboarding/forms/${formId}/import`,
      }),
    }),
    syncTypeformAdminFormDefinition: builder.mutation<
      TypeformAdminDefinitionSyncResult,
      string
    >({
      invalidatesTags: (_result, _error, formId) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
      ],
      query: (formId) => ({
        method: 'POST',
        url: `/admin/onboarding/forms/${formId}/sync_definition`,
      }),
    }),
    backfillTypeformAdminForm: builder.mutation<
      TypeformAdminBackfillResult,
      string
    >({
      invalidatesTags: (_result, _error, formId) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
        'TypeformAdminSubmission',
      ],
      query: (formId) => ({
        method: 'POST',
        url: `/admin/onboarding/forms/${formId}/backfill`,
      }),
    }),
    reclassifyTypeformAdminForm: builder.mutation<
      TypeformAdminReclassifyResult,
      { formId: string; formType: string }
    >({
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
        'TypeformAdminSubmission',
      ],
      query: ({ formId, formType }) => ({
        method: 'POST',
        data: { formType },
        url: `/admin/onboarding/forms/${formId}/reclassify`,
      }),
    }),
    getTypeformAdminFormGroups: builder.query<
      TypeformAdminFormGroupsResponse,
      string
    >({
      providesTags: (_result, _error, formId) => [
        { type: 'TypeformAdminForm' as const, id: formId },
      ],
      query: (formId) => ({
        method: 'GET',
        url: `/admin/onboarding/forms/${formId}/groups`,
      }),
    }),
    setTypeformAdminFormGroups: builder.mutation<
      { groups: TypeformAdminFormFieldGroup[]; canonicalRefs: string[] },
      { formId: string; groups: TypeformAdminFormFieldGroup[] }
    >({
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'TypeformAdminForm' as const, id: formId },
        'TypeformAdminForm',
      ],
      query: ({ formId, groups }) => ({
        method: 'PUT',
        data: { groups },
        url: `/admin/onboarding/forms/${formId}/groups`,
      }),
    }),
  }),
});

export const {
  useGetTypeformAdminSubmissionsQuery,
  useGetTypeformAdminSubmissionQuery,
  useLinkTypeformAdminSubmissionMutation,
  useUnlinkTypeformAdminSubmissionMutation,
  useUpdateTypeformAdminSubmissionStatusMutation,
  useExportTypeformAdminSubmissionsMutation,
  useGetTypeformAdminFormsQuery,
  useSyncTypeformAdminCatalogMutation,
  useUpdateTypeformAdminFormCatalogMutation,
  useImportAllTypeformAdminFormsMutation,
  useGetTypeformAdminFormMappingsQuery,
  useSetTypeformAdminFormMappingsMutation,
  useImportTypeformAdminFormMutation,
  useSyncTypeformAdminFormDefinitionMutation,
  useBackfillTypeformAdminFormMutation,
  useReclassifyTypeformAdminFormMutation,
  useGetTypeformAdminFormGroupsQuery,
  useSetTypeformAdminFormGroupsMutation,
} = typeformAdminApi;
